/**
 * CookieConsentBanner.jsx — GDPR / DPDP compliant consent banner
 * ══════════════════════════════════════════════════════════════════════
 * Shows on first visit. Stores preference in DB (via privacy API) + localStorage.
 * Categories: Essential (always on), Analytics (opt-in), Marketing (opt-in).
 * Users can re-open via Settings to change preferences.
 *
 * Mount in App.jsx or MainLayout.jsx:
 *   <CookieConsentBanner />
 */
import { useState, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { useUpdateBulkConsentMutation, useGetMyConsentsQuery } from '../../api/privacyApi';

const CONSENT_STORAGE_KEY = 'zentrix-cookie-consent';
const CONSENT_VERSION = '1.0';

const CATEGORIES = [
  {
    id: 'cookie_essential',
    name: 'Essential',
    description: 'Required for the application to function. Authentication, security, preferences. Cannot be disabled.',
    required: true,
  },
  {
    id: 'cookie_analytics',
    name: 'Analytics',
    description: 'Help us understand how you use Zentrix so we can improve the product. No personal data is shared with third parties.',
    required: false,
  },
  {
    id: 'cookie_marketing',
    name: 'Marketing',
    description: 'Used to show relevant product updates and feature announcements. You can opt out anytime.',
    required: false,
  },
  {
    id: 'ai_data_processing',
    name: 'AI data processing',
    description: 'Zentrix AI (Zuno) sends workspace data to AI providers (OpenAI, Google, Anthropic) to generate insights. Data is not stored by providers beyond the API call.',
    required: false,
  },
];

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [choices, setChoices] = useState({
    cookie_essential: true,
    cookie_analytics: false,
    cookie_marketing: false,
    ai_data_processing: true,
  });

  const [updateBulkConsent] = useUpdateBulkConsentMutation();
  const { data: existingConsents } = useGetMyConsentsQuery(undefined, {
    skip: typeof window === 'undefined',
  });

  useEffect(() => {
    // Check if consent has been given
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.version === CONSENT_VERSION) return; // Already consented to current version
      } catch {}
    }

    // Check DB consents
    const dbConsents = existingConsents?.data;
    if (dbConsents && Object.keys(dbConsents).length > 0) {
      // User has consents in DB — sync to localStorage and don't show banner
      const synced = { version: CONSENT_VERSION };
      CATEGORIES.forEach(cat => {
        synced[cat.id] = dbConsents[cat.id]?.granted ?? cat.required;
      });
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(synced));
      return;
    }

    // No consent found — show banner after 1 second
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [existingConsents]);

  const handleAcceptAll = async () => {
    const allGranted = {};
    CATEGORIES.forEach(cat => { allGranted[cat.id] = true; });
    await saveConsent(allGranted);
  };

  const handleRejectOptional = async () => {
    const essentialOnly = {};
    CATEGORIES.forEach(cat => { essentialOnly[cat.id] = cat.required; });
    await saveConsent(essentialOnly);
  };

  const handleSaveCustom = async () => {
    await saveConsent(choices);
  };

  const saveConsent = async (consents) => {
    // Save to localStorage immediately
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      ...consents,
      savedAt: new Date().toISOString(),
    }));

    // Save to DB via API
    try {
      const consentArray = Object.entries(consents).map(([consentType, granted]) => ({
        consentType, granted, version: CONSENT_VERSION,
      }));
      await updateBulkConsent({ consents: consentArray, source: 'banner' }).unwrap();
    } catch {
      // Non-fatal — localStorage is the fallback
    }

    setVisible(false);
  };

  const toggle = (id) => {
    if (CATEGORIES.find(c => c.id === id)?.required) return;
    setChoices(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4">
      <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
        {/* Main bar */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            <Shield size={18} className="text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-800">Privacy preferences</h3>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                We use cookies and process data to provide our service. Some are essential,
                others help us improve your experience. You can customize your preferences below.
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-3">
            <button onClick={handleAcceptAll}
              className="px-4 py-2 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors">
              Accept all
            </button>
            <button onClick={handleRejectOptional}
              className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Essential only
            </button>
            <button onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-gray-700 transition-colors ml-auto">
              Customize {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>
        </div>

        {/* Expanded preferences */}
        {expanded && (
          <div className="border-t border-gray-100 p-4 space-y-2">
            {CATEGORIES.map(cat => (
              <div key={cat.id}
                className={`flex items-start gap-3 p-3 rounded-lg ${
                  choices[cat.id] ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'
                }`}>
                <button
                  onClick={() => toggle(cat.id)}
                  disabled={cat.required}
                  className={`w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 border-2 transition-colors ${
                    choices[cat.id]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 bg-white'
                  } ${cat.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  {choices[cat.id] && <Check size={11} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-800">{cat.name}</span>
                    {cat.required && (
                      <span className="text-[11px] font-semibold text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-600 mt-0.5 leading-relaxed">{cat.description}</p>
                </div>
              </div>
            ))}

            <button onClick={handleSaveCustom}
              className="w-full py-2.5 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors mt-2">
              Save preferences
            </button>

            <p className="text-[11px] text-gray-600 text-center mt-1">
              You can change these preferences anytime in Settings. See our{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
